import { z } from 'zod';
import { UserIdSchema } from './common';

/**
 * Módulo de payroll / asistencia de enfermería.
 *
 * Reglas del producto (acordadas con Endomedical):
 *  - Cada enfermera marca entrada/salida en la plataforma.
 *  - Tarifa diaria base = `tarifaJornadaNormal` (por enfermera).
 *  - Bono "más de 20 pacientes" = se suma cuando los procedimientos del día
 *    superan 20 (estricto). Patient count == procedure count del día.
 *  - Bono "hora extra" = se suma cuando la enfermera marca SALIDA después
 *    de las 2:00 p.m. hora Colombia (UTC-5).
 *  - Quincenas: día 1–15 y día 16–último.
 *  - Histórico: todas las jornadas quedan almacenadas, no se borran.
 *
 * Importante sobre zona horaria: la zona Colombia es fija UTC-5 (sin DST).
 * El backend almacena timestamps con TZ; las claves de "día" (la fecha de
 * la jornada) se derivan con offset -05:00 para evitar que una jornada que
 * cruza UTC midnight aparezca en dos días distintos en reporte.
 */

/** Tarifas (en pesos colombianos enteros). */
export const NurseRatesSchema = z.object({
  nurseId: UserIdSchema,
  nurseNombre: z.string().optional(),
  tarifaJornadaNormal: z.number().int().min(0).max(10_000_000),
  tarifaAdicionalMas20Pacientes: z.number().int().min(0).max(10_000_000),
  tarifaHoraExtraDespues2pm: z.number().int().min(0).max(10_000_000),
  updatedAt: z.string().datetime().optional(),
});
export type NurseRates = z.infer<typeof NurseRatesSchema>;

export const UpdateNurseRatesSchema = NurseRatesSchema.omit({
  nurseId: true,
  nurseNombre: true,
  updatedAt: true,
}).partial();
export type UpdateNurseRatesInput = z.infer<typeof UpdateNurseRatesSchema>;

/** Una jornada (turno) de una enfermera. */
export const NurseShiftSchema = z.object({
  id: z.string().uuid(),
  nurseId: UserIdSchema,
  nurseNombre: z.string().optional(),
  /** Fecha del turno en formato YYYY-MM-DD según zona Colombia. */
  fecha: z.string().date(),
  /** Timestamp ISO de la entrada. */
  inicioAt: z.string().datetime(),
  /** Timestamp ISO de la salida. Null cuando el turno está abierto. */
  finAt: z.string().datetime().nullable(),
  /** Notas opcionales que el admin o la enfermera quiera agregar. */
  notas: z.string().max(500).optional(),
});
export type NurseShift = z.infer<typeof NurseShiftSchema>;

/**
 * Body de `POST /api/payroll/clock-in`. La cuenta compartida de enfermería
 * marca la entrada para una enfermera específica del directorio.
 */
export const NurseClockInSchema = z.object({
  nurseId: UserIdSchema,
});
export type NurseClockInInput = z.infer<typeof NurseClockInSchema>;

/** Body de `POST /api/payroll/clock-out`. */
export const NurseClockOutSchema = z.object({
  nurseId: UserIdSchema,
  notas: z.string().max(500).optional(),
});
export type NurseClockOutInput = z.infer<typeof NurseClockOutSchema>;

/** Hora local Colombia en formato HH:mm (24h). */
export const HoraColombiaSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida (usa HH:mm)');

/**
 * Body de `POST /api/payroll/shifts` — el admin registra manualmente una
 * jornada (p. ej. la enfermera olvidó marcar entrada/salida ese día, incluso
 * si es un día anterior). Las horas son hora local Colombia (UTC-5).
 * `horaFin` null u omitida ⇒ jornada abierta (sin salida registrada).
 */
export const AdminCreateShiftSchema = z.object({
  nurseId: UserIdSchema,
  fecha: z.string().date(),
  horaInicio: HoraColombiaSchema,
  horaFin: HoraColombiaSchema.nullable().optional(),
  notas: z.string().max(500).optional(),
});
export type AdminCreateShiftInput = z.infer<typeof AdminCreateShiftSchema>;

/**
 * Body de `PATCH /api/payroll/shifts/:id` — el admin ajusta una jornada
 * existente. `horaFin` = string cierra/corrige la salida; `horaFin` = null
 * reabre la jornada (deja la salida sin registrar); campo omitido = sin cambio.
 * La fecha y la enfermera del turno no se modifican.
 */
export const AdminEditShiftSchema = z.object({
  horaInicio: HoraColombiaSchema.optional(),
  horaFin: HoraColombiaSchema.nullable().optional(),
  notas: z.string().max(500).optional(),
});
export type AdminEditShiftInput = z.infer<typeof AdminEditShiftSchema>;

/** Query de `GET /api/payroll/current-shift?nurseId=`. */
export const CurrentShiftQuerySchema = z.object({
  nurseId: UserIdSchema,
});
export type CurrentShiftQuery = z.infer<typeof CurrentShiftQuerySchema>;

/** Respuesta de "turno actual" para una enfermera del directorio. */
export const CurrentShiftSchema = z.object({
  shift: NurseShiftSchema.nullable(),
  /** Si hay turno abierto, número de procedimientos del día (a la fecha). */
  procedimientosHoy: z.number().int().min(0),
});
export type CurrentShift = z.infer<typeof CurrentShiftSchema>;

/** Linea de detalle del paystub: una fila por día trabajado. */
export const PayrollLineSchema = z.object({
  fecha: z.string().date(),
  inicioAt: z.string().datetime(),
  finAt: z.string().datetime().nullable(),
  /** Procedimientos atendidos en la sala ese día. */
  procedimientos: z.number().int().min(0),
  /** Hora de salida en HH:mm zona Colombia. Null si no ha salido. */
  horaSalidaCol: z.string().nullable(),
  /** Flags calculados. */
  aplicaJornadaNormal: z.boolean(),
  aplicaMas20Pacientes: z.boolean(),
  aplicaHoraExtra: z.boolean(),
  /** Subtotal del día (en pesos enteros). */
  subtotal: z.number().int().min(0),
});
export type PayrollLine = z.infer<typeof PayrollLineSchema>;

/** Paystub quincenal completo. */
export const PayrollPaystubSchema = z.object({
  nurseId: UserIdSchema,
  nurseNombre: z.string(),
  rates: NurseRatesSchema,
  /** Inicio y fin del periodo en formato YYYY-MM-DD. */
  periodoInicio: z.string().date(),
  periodoFin: z.string().date(),
  /** Quincena: 1 (días 1–15) o 2 (días 16–último). */
  quincena: z.union([z.literal(1), z.literal(2)]),
  /** Lineas detalladas por día trabajado (en orden). */
  lineas: z.array(PayrollLineSchema),
  /** Totales agregados. */
  diasTrabajados: z.number().int().min(0),
  totalJornadaNormal: z.number().int().min(0),
  totalAdicionalMas20: z.number().int().min(0),
  totalHoraExtra: z.number().int().min(0),
  total: z.number().int().min(0),
});
export type PayrollPaystub = z.infer<typeof PayrollPaystubSchema>;

/**
 * Liquidación consolidada de la quincena para todas las enfermeras —
 * lo que el admin necesita ver al final del periodo: cuánto total se paga
 * en esta quincena por todo el equipo, y cómo se distribuye por persona.
 */
export const PayrollPeriodTotalSchema = z.object({
  periodoInicio: z.string().date(),
  periodoFin: z.string().date(),
  quincena: z.union([z.literal(1), z.literal(2)]),
  paystubs: z.array(PayrollPaystubSchema),
  totalPeriodo: z.number().int().min(0),
  totalJornadaNormal: z.number().int().min(0),
  totalAdicionalMas20: z.number().int().min(0),
  totalHoraExtra: z.number().int().min(0),
  totalEnfermerasConJornada: z.number().int().min(0),
  totalDiasTrabajados: z.number().int().min(0),
});
export type PayrollPeriodTotal = z.infer<typeof PayrollPeriodTotalSchema>;

/**
 * Helper de zona Colombia: dado un Date (UTC), devuelve YYYY-MM-DD según
 * UTC-5. Útil para agrupar jornadas por "día Colombia" sin importar dónde
 * corra el servidor. Exportado aquí para que frontend y backend compartan
 * la misma implementación.
 */
export const COLOMBIA_UTC_OFFSET_MINUTES = -5 * 60;

export function colombiaDateString(date: Date): string {
  const adjusted = new Date(date.getTime() + COLOMBIA_UTC_OFFSET_MINUTES * 60 * 1000);
  const y = adjusted.getUTCFullYear();
  const m = String(adjusted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(adjusted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function colombiaHourMinute(date: Date): string {
  const adjusted = new Date(date.getTime() + COLOMBIA_UTC_OFFSET_MINUTES * 60 * 1000);
  const h = String(adjusted.getUTCHours()).padStart(2, '0');
  const m = String(adjusted.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Construye el instante (ISO UTC) correspondiente a una fecha + hora local
 * Colombia (UTC-5, sin DST). Ej: ('2026-07-15', '08:00') →
 * '2026-07-15T13:00:00.000Z'. Se usa para registrar/ajustar jornadas
 * manualmente cuando el admin escribe la hora que la enfermera realmente marcó.
 */
export function colombiaLocalToInstant(fecha: string, hora: string): string {
  return new Date(`${fecha}T${hora}:00-05:00`).toISOString();
}

/** Devuelve true si la hora local Colombia es ≥ 14:00. */
export function isAfter2pmColombia(date: Date): boolean {
  const adjusted = new Date(date.getTime() + COLOMBIA_UTC_OFFSET_MINUTES * 60 * 1000);
  return adjusted.getUTCHours() >= 14;
}
